import { isNil } from 'lodash'
import { type BadgeReward } from 'types/api'
import { useParams } from 'react-router-dom'
import { Box } from '@mui/material'
import { useMemo, useState } from 'react'
import { DataTable } from 'components/Shared/DataTable'
import { PatronCheckInBadgeDetailsModal } from './PatronCheckInBadgeDetailsModal'
import { useGetPatronById } from 'hooks/api/Patron/useGetPatronById'
import { useGetBadgeRewards } from 'hooks/api/Contests/BadgeReward/useGetBadgeRewards'
import { useGetPatronBadgeRewardsByPatronId } from 'hooks/api/Contests/BadgeReward/useGetPatronBadgeRewardsByPatronId'
import type { GridColDef, GridEventListener } from '@mui/x-data-grid'
import { ActivityIndicator } from 'components/Shared/ActivityIndicator'
import { formatFullDateTime } from 'utils/util'
import GiftIcon from 'assets/gift_empty_state.svg'
import { SectionHeader } from 'components/SectionHeader'
import { PatronDetailsHeader } from '../DetailsHeader/PatronDetailsHeader'
import { PatronProfileBadgeDetailsModal } from './PatronProfileBadgeDetailsModal'
import { PatronReferralBadgeDetailsModal } from './PatronReferralBadgeDetailsModal'
import { PatronLocationBadgeDetailsModal } from './PatronLocationBadgeDetailsModal'
import { useGetAllCorporateAccounts } from 'hooks/api/useGetAllCorporateAccounts'
import { useGetAllOrganizations } from 'hooks/api/useGetAllOrganizations'
import { useGetAllLocationBadges } from 'hooks/api/Contests/BadgeReward/useGetAllLocationBadges'
import { PatronAwardOrProgressBadgeModal } from './PatronAwardOrProgressBadgeModal'

// add isMobile: boolean to the parameter
const getColumns = (
  corporateAccountsMap: Map<number, string>,
  organizationsMap: Map<number, string>
): GridColDef[] => [
  {
    field: 'name',
    headerName: 'Badge Name',
    minWidth: 120,
    flex: 2,
  },
  {
    field: 'createdOn',
    headerName: 'Most Recent Date Earned',
    minWidth: 120,
    flex: 1.5,
    valueFormatter: (params: { value: string }) => {
      const date = formatFullDateTime(params.value)
      return date
    },
  },
  {
    field: 'count',
    headerName: 'Times Earned',
    minWidth: 120,
    flex: 1,
  },
  {
    field: 'subType',
    headerName: 'Locations',
    minWidth: 215,
    flex: 1,
    valueGetter: (params) => {
      if (params.row.subtype === 'Location') {
        const badgeLEs = params.row.badgeLEs ?? []
        return badgeLEs.length === 1
          ? `${String(badgeLEs[0]?.licensedEstablishment?.standardName)}`
          : // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
            `${badgeLEs.length} Location(s)`
      } else if (params.row.subtype === 'CorporateAccount') {
        return params.row.corporateAccountId
          ? (corporateAccountsMap.get(params.row.corporateAccountId) ?? '')
          : ''
      } else if (params.row.subtype === 'Organization') {
        return params.row.organizationId
          ? (organizationsMap.get(params.row.organizationId) ?? '')
          : ''
      }
      return ''
    },
  },
]

export const PatronBadgesPage = () => {
  const { id: patronAccountId } = useParams()
  // const { isMobile } = useLayout()
  const [isCheckInBadgeModalOpen, setIsCheckInBadgeModalOpen] = useState(false)
  const [isProfileBadgeModalOpen, setIsProfileBadgeModalOpen] = useState(false)
  const [isReferralBadgeModalOpen, setIsReferralBadgeModalOpen] =
    useState(false)
  const [isLocationBadgeModalOpen, setIsLocationBadgeModalOpen] =
    useState(false)
  const [isAwardOrProgressBadgeModalOpen, setIsAwardOrProgressBadgeModalOpen] =
    useState(false)
  const [awardOrProgressBadgeModalType, setAwardOrProgressBadgeModalType] =
    useState<'Award' | 'Progress'>('Award')

  const [selectedBadgeReward, setSelectedBadgeReward] =
    useState<BadgeReward | null>(null)

  const toggleCheckInBadgeModalIsOpen = () => {
    setIsCheckInBadgeModalOpen(!isCheckInBadgeModalOpen)
  }

  const toggleProfileBadgeModalIsOpen = () => {
    setIsProfileBadgeModalOpen(!isProfileBadgeModalOpen)
  }

  const toggleReferralBadgeModalIsOpen = () => {
    setIsReferralBadgeModalOpen(!isReferralBadgeModalOpen)
  }

  const toggleLocationBadgeModalOpen = () => {
    setIsLocationBadgeModalOpen(!isLocationBadgeModalOpen)
  }

  const toggleAwardOrProgressBadgeModalIsOpen = () => {
    setIsAwardOrProgressBadgeModalOpen(!isAwardOrProgressBadgeModalOpen)
  }

  const patronQuery = useGetPatronById(Number(patronAccountId))
  const badgeRewardsQuery = useGetBadgeRewards()
  const patronBadgeRewardsQuery = useGetPatronBadgeRewardsByPatronId({
    patronId: Number(patronAccountId),
  })
  const patronBadgeRewards = patronBadgeRewardsQuery.data ?? []
  const allBadgeRewards = badgeRewardsQuery.data ?? []

  const corporateAccountsQuery = useGetAllCorporateAccounts()
  const organizationsQuery = useGetAllOrganizations()
  const locationBadgesQuery = useGetAllLocationBadges()

  const corporateAccountsMap = useMemo(() => {
    const map = new Map<number, string>()
    corporateAccountsQuery.data?.forEach((ca) => {
      if (ca.id && ca.name) map.set(ca.id, ca.name)
    })
    return map
  }, [corporateAccountsQuery.data])

  const organizationsMap = useMemo(() => {
    const map = new Map<number, string>()
    organizationsQuery.data?.forEach((org) => {
      if (org.id && org.name) map.set(org.id, org.name)
    })
    return map
  }, [organizationsQuery.data])

  const badgeColumns = useMemo(
    () => getColumns(corporateAccountsMap, organizationsMap),
    [corporateAccountsMap, organizationsMap]
  )
  const nonLocationBadgeColumns = useMemo(
    () => badgeColumns.filter((column) => column.field !== 'subType'),
    [badgeColumns]
  )

  if (
    patronQuery.isPending ||
    badgeRewardsQuery.isPending ||
    patronBadgeRewardsQuery.isPending
  ) {
    return <ActivityIndicator />
  }

  if (
    patronQuery.isError ||
    patronBadgeRewardsQuery.isError ||
    badgeRewardsQuery.isError ||
    isNil(patronQuery.data)
  ) {
    return <p>An error occurred.</p>
  }

  const selectRow: GridEventListener<'rowClick'> = (params) => {
    // get the badge that was clicked on
    const selectedBadge =
      allBadgeRewards.find((br) => br.id === params.row.id) ?? null
    // set selected badge to state
    setSelectedBadgeReward(selectedBadge)
    // toggle the correct modal based on badge type
    switch (selectedBadge?.type) {
      case 'CheckIn':
      case 'CheckInStreak':
        toggleCheckInBadgeModalIsOpen()
        break
      case 'PatronProfile_Anniversary':
      case 'PatronProfile_Address':
      case 'PatronProfile_Birthday':
        toggleProfileBadgeModalIsOpen()
        break
      case 'Referral_Count':
        toggleReferralBadgeModalIsOpen()
        break
      case 'LocationCheckIn':
      case 'LocationCheckInStreak':
      case 'LocationUniqueCheckIn':
      case 'UniqueLocationVisits':
        toggleLocationBadgeModalOpen()
        break
      default:
        break
    }
  }

  // Build a dictionary of counts and latest date for each badge for the patron
  // Key badgeRewardId, Value object with count and latestDate
  const patronBadgeLookup = patronBadgeRewards.reduce<
    Record<string, { count: number; latestDate: string | null }>
  >((acc, pbr) => {
    const badgeRewardId = Number(pbr.badgeRewardId)
    const createdOn = String(pbr.createdOn)
    if (!acc[badgeRewardId]) {
      acc[badgeRewardId] = { count: 0, latestDate: null as string | null }
    }

    acc[badgeRewardId].count += 1

    if (
      !acc[badgeRewardId].latestDate ||
      new Date(createdOn) > new Date(acc[badgeRewardId].latestDate)
    ) {
      acc[badgeRewardId].latestDate = createdOn
    }

    return acc
  }, {})

  // Create a new array of objects for all badges with counts and latest date the patron earned it
  const badgesWithCounts = allBadgeRewards.map((badge) => {
    const stats = patronBadgeLookup[Number(badge.id)] || {
      count: 0,
      latestDate: null,
    }

    return {
      id: badge.id,
      name: badge.name,
      type: badge.type,
      count: stats.count,
      createdOn: stats.latestDate,
    }
  })

  // Split into arrays by type
  const checkInBadgeRewards = badgesWithCounts.filter(
    (b) => b.type === 'CheckIn' || b.type === 'CheckInStreak'
  )
  const profileBadgeRewards = badgesWithCounts.filter(
    (b) =>
      b.type === 'PatronProfile_Anniversary' ||
      b.type === 'PatronProfile_Address' ||
      b.type === 'PatronProfile_Birthday'
  )
  const referralBadgeRewards = badgesWithCounts.filter(
    (b) => b.type === 'Referral_Count'
  )

  const locationBadgeRewards = (locationBadgesQuery.data ?? [])
    .filter(
      (b) =>
        b.type === 'LocationCheckIn' ||
        b.type === 'LocationCheckInStreak' ||
        b.type === 'LocationUniqueCheckIn' ||
        b.type === 'UniqueLocationVisits'
    )
    .filter((badge) => patronBadgeLookup[Number(badge.id)] !== undefined)
    .map((badge) => {
      const stats = patronBadgeLookup[Number(badge.id)] || {
        count: 0,
        latestDate: null,
      }

      return {
        ...badge,
        count: stats.count,
        createdOn: stats.latestDate,
      }
    })

  const previousBadgesEarnedForSelectedBadge = patronBadgeRewards.filter(
    (badgeReward) => badgeReward.badgeRewardId === selectedBadgeReward?.id
  )

  return (
    <PatronDetailsHeader
      patron={patronQuery.data}
      currentTab={`/PatronAccounts/${patronAccountId}/Badges`}
    >
      <Box
        className="flex flex-col w-full sm:flex-row sm:justify-between sm:items-start"
        paddingTop={{ xs: 2, sm: 3 }}
      >
        <SectionHeader title="Check In" showButton={false} />
      </Box>
      <DataTable
        columns={nonLocationBadgeColumns}
        rows={checkInBadgeRewards}
        onRowClick={selectRow}
        noDataMessage="No Check In Badges have been earned yet."
        emptyDataImageSource={GiftIcon}
      />

      <SectionHeader
        sx={{ paddingTop: { xs: 5, sm: 6 } }}
        title="Profile"
        showButton={false}
      />
      <DataTable
        columns={nonLocationBadgeColumns}
        rows={profileBadgeRewards}
        onRowClick={selectRow}
        noDataMessage="No Profile Badges have been earned yet."
        emptyDataImageSource={GiftIcon}
      />

      <SectionHeader
        sx={{ paddingTop: { xs: 5, sm: 6 } }}
        title="Referral"
        showButton={false}
      />
      <DataTable
        columns={nonLocationBadgeColumns}
        rows={referralBadgeRewards}
        onRowClick={selectRow}
        noDataMessage="No Referral Badges have been earned yet."
        emptyDataImageSource={GiftIcon}
      />

      <SectionHeader
        sx={{ paddingTop: { xs: 5, sm: 6 } }}
        title="Location"
        buttonText={'Award Badge'}
        onClickButton={async () => {
          setAwardOrProgressBadgeModalType('Award')
          toggleAwardOrProgressBadgeModalIsOpen()
        }}
        showSecondaryButton={true}
        secondaryButtonText={'Badge Progress'}
        onClickSecondaryButton={async () => {
          setAwardOrProgressBadgeModalType('Progress')
          toggleAwardOrProgressBadgeModalIsOpen()
        }}
      />
      <DataTable
        columns={badgeColumns} // add isMobile to the parameter
        rows={locationBadgeRewards}
        onRowClick={selectRow}
        noDataMessage="No Location Badges have been earned yet."
        emptyDataImageSource={GiftIcon}
      />

      {selectedBadgeReward &&
        (selectedBadgeReward.type === 'CheckIn' ||
          selectedBadgeReward.type === 'CheckInStreak') && (
          <PatronCheckInBadgeDetailsModal
            patronId={Number(patronAccountId)}
            selectedBadgeReward={selectedBadgeReward}
            previousBadgesEarned={previousBadgesEarnedForSelectedBadge}
            isModalOpen={isCheckInBadgeModalOpen}
            toggleModalIsOpen={toggleCheckInBadgeModalIsOpen}
          />
        )}
      {selectedBadgeReward &&
        (selectedBadgeReward.type === 'PatronProfile_Address' ||
          selectedBadgeReward.type === 'PatronProfile_Birthday' ||
          selectedBadgeReward.type === 'PatronProfile_Anniversary') && (
          <PatronProfileBadgeDetailsModal
            patronId={Number(patronAccountId)}
            selectedBadgeReward={selectedBadgeReward}
            previousBadgesEarned={previousBadgesEarnedForSelectedBadge}
            isModalOpen={isProfileBadgeModalOpen}
            toggleModalIsOpen={toggleProfileBadgeModalIsOpen}
          />
        )}

      {selectedBadgeReward && selectedBadgeReward?.type === 'Referral_Count' ? (
        <PatronReferralBadgeDetailsModal
          patronId={Number(patronAccountId)}
          selectedBadgeReward={selectedBadgeReward}
          previousBadgesEarned={previousBadgesEarnedForSelectedBadge}
          isModalOpen={isReferralBadgeModalOpen}
          toggleModalIsOpen={toggleReferralBadgeModalIsOpen}
        />
      ) : null}

      {selectedBadgeReward &&
        (selectedBadgeReward.type === 'LocationCheckIn' ||
          selectedBadgeReward.type === 'LocationCheckInStreak' ||
          selectedBadgeReward.type === 'LocationUniqueCheckIn' ||
          selectedBadgeReward.type === 'UniqueLocationVisits') && (
          <PatronLocationBadgeDetailsModal
            patronId={Number(patronAccountId)}
            selectedBadgeReward={selectedBadgeReward}
            previousBadgesEarned={previousBadgesEarnedForSelectedBadge}
            isModalOpen={isLocationBadgeModalOpen}
            toggleModalIsOpen={toggleLocationBadgeModalOpen}
          />
        )}

      <PatronAwardOrProgressBadgeModal
        patronId={Number(patronAccountId)}
        type={awardOrProgressBadgeModalType}
        isModalOpen={isAwardOrProgressBadgeModalOpen}
        toggleModalIsOpen={toggleAwardOrProgressBadgeModalIsOpen}
      />
    </PatronDetailsHeader>
  )
}
